/** Hub org calendar for snooze presets (matches backend). */
export const HUB_NOTIFICATION_SNOOZE_TIMEZONE = "America/New_York";

export type NotificationSnoozePreset = "tomorrow" | "next_monday";

type CalendarDate = {
  year: number;
  month: number;
  day: number;
};

function calendarDatePartsInTimeZone(date: Date, timeZone: string): CalendarDate & { weekday: number } {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(date).filter((part) => part.type !== "literal").map((part) => [part.type, part.value]),
  );
  const weekdayMap: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };
  const weekday = weekdayMap[parts.weekday ?? ""] ?? 1;
  return {
    year: Number.parseInt(parts.year ?? "1970", 10),
    month: Number.parseInt(parts.month ?? "1", 10),
    day: Number.parseInt(parts.day ?? "1", 10),
    weekday,
  };
}

function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

function timeZoneOffsetMinutes(timeZone: string, utcDate: Date): number {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
    hour: "2-digit",
  });
  const part = formatter.formatToParts(utcDate).find((entry) => entry.type === "timeZoneName")?.value ?? "GMT";
  const match = part.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/i);
  if (match == null) {
    return 0;
  }
  const sign = match[1] === "-" ? -1 : 1;
  const hours = Number.parseInt(match[2] ?? "0", 10);
  const minutes = Number.parseInt(match[3] ?? "0", 10);
  return sign * (hours * 60 + minutes);
}

export function zonedMidnightToUtcIso(
  date: CalendarDate,
  timeZone: string = HUB_NOTIFICATION_SNOOZE_TIMEZONE,
): string {
  let utcMs = Date.UTC(date.year, date.month - 1, date.day, 0, 0, 0);
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const offsetMinutes = timeZoneOffsetMinutes(timeZone, new Date(utcMs));
    utcMs = Date.UTC(date.year, date.month - 1, date.day, 0, 0, 0) - offsetMinutes * 60_000;
  }
  return new Date(utcMs).toISOString();
}

export function snoozePresetUntilIso(
  preset: NotificationSnoozePreset,
  now: Date = new Date(),
): string {
  const today = calendarDatePartsInTimeZone(now, HUB_NOTIFICATION_SNOOZE_TIMEZONE);
  if (preset === "tomorrow") {
    return zonedMidnightToUtcIso(addCalendarDays(today, 1));
  }
  let daysUntilNextMonday = (8 - today.weekday) % 7;
  if (daysUntilNextMonday === 0) {
    daysUntilNextMonday = 7;
  }
  return zonedMidnightToUtcIso(addCalendarDays(today, daysUntilNextMonday));
}

export function datetimeLocalValueToIso(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") {
    throw new Error("Pick a date and time.");
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Pick a valid date and time.");
  }
  return parsed.toISOString();
}

export function isNotificationActivelySnoozed(
  snoozedUntil: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (snoozedUntil == null || snoozedUntil.trim() === "") {
    return false;
  }
  const untilMs = new Date(snoozedUntil).getTime();
  return !Number.isNaN(untilMs) && untilMs > nowMs;
}

export function formatSnoozedUntilLabel(
  snoozedUntil: string,
  nowMs: number = Date.now(),
): string {
  const until = new Date(snoozedUntil);
  if (Number.isNaN(until.getTime())) {
    return "Snoozed";
  }
  const msUntil = until.getTime() - nowMs;
  if (msUntil <= 0) {
    return "Snoozed";
  }
  if (msUntil >= 7 * 86_400_000) {
    const weekday = until.toLocaleDateString(undefined, { weekday: "short" });
    const time = until.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `Snoozed until ${weekday} ${time}`;
  }
  const totalDays = msUntil / 86_400_000;
  if (totalDays >= 1) {
    return `Snoozed · ${String(Math.ceil(totalDays))}d`;
  }
  const hoursUntil = Math.max(1, Math.ceil(msUntil / 3_600_000));
  return `Snoozed · ${String(hoursUntil)}h`;
}

export function defaultCustomSnoozeLocalValue(now: Date = new Date()): string {
  const nextHour = new Date(now.getTime() + 60 * 60_000);
  nextHour.setMinutes(0, 0, 0);
  const year = nextHour.getFullYear();
  const month = String(nextHour.getMonth() + 1).padStart(2, "0");
  const day = String(nextHour.getDate()).padStart(2, "0");
  const hour = String(nextHour.getHours()).padStart(2, "0");
  const minute = String(nextHour.getMinutes()).padStart(2, "0");
  return `${String(year)}-${month}-${day}T${hour}:${minute}`;
}
